import { useState, useCallback, useRef, useEffect } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import FindChildcare from "./FindChildcare";
import Explore from "./Explore";
import Travel from "./Travel";

const US_CITIES = [
  "Atlanta, GA", "Austin, TX", "Baltimore, MD", "Boston, MA", "Charlotte, NC",
  "Chicago, IL", "Cincinnati, OH", "Cleveland, OH", "Columbus, OH", "Dallas, TX",
  "Denver, CO", "Detroit, MI", "El Paso, TX", "Fort Worth, TX", "Fresno, CA",
  "Houston, TX", "Indianapolis, IN", "Jacksonville, FL", "Kansas City, MO",
  "Las Vegas, NV", "Long Beach, CA", "Los Angeles, CA", "Louisville, KY",
  "Memphis, TN", "Mesa, AZ", "Miami, FL", "Milwaukee, WI", "Minneapolis, MN",
  "Nashville, TN", "New Orleans, LA", "New York, NY", "Newark, NJ", "Oakland, CA",
  "Oklahoma City, OK", "Omaha, NE", "Orlando, FL", "Philadelphia, PA", "Phoenix, AZ",
  "Pittsburgh, PA", "Portland, OR", "Raleigh, NC", "Sacramento, CA",
  "San Antonio, TX", "San Diego, CA", "San Francisco, CA", "San Jose, CA",
  "Seattle, WA", "St. Louis, MO", "Tampa, FL", "Tucson, AZ", "Tulsa, OK",
  "Virginia Beach, VA", "Washington, DC", "Albuquerque, NM", "Anchorage, AK",
  "Arlington, TX", "Aurora, CO", "Bakersfield, CA", "Boise, ID", "Buffalo, NY",
  "Chandler, AZ", "Chula Vista, CA", "Durham, NC", "Fort Wayne, IN",
  "Garland, TX", "Gilbert, AZ", "Glendale, AZ", "Greensboro, NC", "Henderson, NV",
  "Hialeah, FL", "Honolulu, HI", "Irving, TX", "Laredo, TX", "Lexington, KY",
  "Lincoln, NE", "Lubbock, TX", "Madison, WI", "Norfolk, VA", "North Las Vegas, NV",
  "Plano, TX", "Richmond, VA", "Riverside, CA", "Rochester, NY",
  "Salt Lake City, UT", "Scottsdale, AZ", "Shreveport, LA", "Spokane, WA",
  "Stockton, CA", "St. Paul, MN", "Winston-Salem, NC"
];

const POPULAR_GYMS = [
  "Planet Fitness", "LA Fitness", "24 Hour Fitness", "Anytime Fitness",
  "Gold's Gym", "Equinox", "YMCA", "Crunch Fitness", "Orange Theory",
  "CrossFit", "Life Time Fitness", "Bally Total Fitness", "Snap Fitness",
  "Pure Barre", "F45 Training", "Barry's Bootcamp", "SoulCycle", "Burn Boot Camp"
];

const FIELD_INFO = {
  city: { title: "City / Market", body: "Enter any US city and state (e.g. Austin, TX). RentCast pulls real active listings from that market." },
  movingDate: { title: "Moving Date", body: "When you plan to move. Used by the AI to project future rent trends." },
  beds: { title: "Bedrooms", body: "Select all bedroom counts you would consider. Leave blank for any size." },
  baths: { title: "Bathrooms", body: "Select all bathroom counts. 1.5+ includes homes with a half bath. Leave blank for any count." },
  listingType: { title: "Rent or Buy", body: "Rent shows active rental listings. Buy shows homes for sale. Powered by RentCast live MLS data." },
  housingType: { title: "Housing Type", body: "Apartment: multi-family building. Condo: individually owned unit. House: detached single-family. Townhouse: attached multi-floor. Duplex: two-unit building." },
  maxRent: { title: "Max Monthly Rent", body: "Your all-in monthly budget for rentals. Listings at or under this are shown as matching." },
  maxListPrice: { title: "Max List Price", body: "Maximum purchase price for homes for sale." },
  commute: { title: "Commute to Work", body: "Enter your work address and max commute time. The AI will factor drive time from each neighborhood to your workplace." },
  grocery: { title: "Max Drive to Premium Grocery", body: "Drive time to HEB, Publix, Harris Teeter, Whole Foods, Trader Joes, Wegmans, or Sprouts. Walmart excluded." },
  daycare: { title: "Max Drive to Daycare", body: "Drive time to the nearest licensed daycare." },
  gym: { title: "Max Drive to Gym", body: "Drive time to the nearest gym or fitness center. You can specify a preferred gym chain." },
  crime: { title: "Crime Threshold", body: "Bottom 20% means the neighborhood is in the lowest 20% for crime in that city." },
  schools: { title: "School District Rating", body: "School district quality relative to all districts in the state." },
  score: { title: "Match Score", body: "0 to 100 score showing how well this listing matches your filters. 90 plus is excellent." },
  extra: { title: "Additional Requirements", body: "Free-text for anything else: dog park nearby, no carpet, EV charging, pool, in-unit washer dryer." }
};

// ─── Icons ────────────────────────────────────────────────────────────────────
const Icon = ({ name, size = 16, color = "currentColor" }) => {
  const icons = {
    home: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
    heart: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>,
    search: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
    map: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg>,
    bed: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 4v16"/><path d="M2 8h18a2 2 0 0 1 2 2v10"/><path d="M2 17h20"/><path d="M6 8v9"/></svg>,
    bath: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6 6.5 3.5a1.5 1.5 0 0 0-1-.5C4.683 3 4 3.683 4 4.5V17a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5"/><line x1="10" y1="5" x2="8" y2="7"/><line x1="2" y1="12" x2="22" y2="12"/></svg>,
    calendar: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
    settings: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="12" x2="20" y2="12"/><line x1="4" y1="18" x2="20" y2="18"/></svg>,
    pin: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
    car: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>,
    grocery: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>,
    baby: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="5"/><path d="M20 21a8 8 0 1 0-16 0"/></svg>,
    gym: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8h1a4 4 0 0 1 0 8h-1"/><path d="M2 8h1a4 4 0 0 1 4 4H6a4 4 0 0 1-4-4z"/><path d="M6 8h12v8H6z"/><line x1="12" y1="8" x2="12" y2="16"/></svg>,
    shield: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
    school: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m4 6 8-4 8 4"/><path d="m18 10 4 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-8l4-2"/><path d="M14 22v-4a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v4"/><path d="M18 5v17"/><path d="M6 5v17"/><circle cx="12" cy="9" r="2"/></svg>,
    star: <svg width={size} height={size} viewBox="0 0 24 24" fill={color} stroke={color} strokeWidth="1"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
    sparkle: <svg width={size} height={size} viewBox="0 0 24 24" fill={color}><path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z"/></svg>,
    info: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>,
    chevronDown: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>,
    external: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>,
    target: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>,
    share: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>,
    check: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
  };
  return icons[name] || null;
};

function InfoPopup({ field }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const info = FIELD_INFO[field];
  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);
  if (!info) return null;
  return (
    <span ref={ref} style={{ position: "relative", display: "inline-flex", alignItems: "center", marginLeft: 5 }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: 18, height: 18, borderRadius: "50%", border: "none",
        background: open ? "var(--accent)" : "var(--chip-bg)", color: open ? "#fff" : "var(--muted)",
        fontSize: 10, fontWeight: 700, cursor: "pointer", display: "inline-flex",
        alignItems: "center", justifyContent: "center", padding: 0, transition: "all .15s",
        flexShrink: 0
      }}>
        <Icon name="info" size={11} color={open ? "#fff" : "var(--muted)"} />
      </button>
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 8px)", left: "50%", transform: "translateX(-50%)",
          zIndex: 300, background: "var(--surface-dark)", color: "#fff", borderRadius: 12,
          padding: "12px 14px", width: 240, boxShadow: "0 8px 32px rgba(108,71,255,.18)",
          fontFamily: "var(--font-body)",
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--accent-light)", marginBottom: 5 }}>{info.title}</div>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,.75)", lineHeight: 1.7 }}>{info.body}</div>
          <div style={{ position: "absolute", top: -5, left: "50%", transform: "translateX(-50%)", width: 10, height: 10, background: "var(--surface-dark)", rotate: "45deg", borderRadius: 2 }} />
        </div>
      )}
    </span>
  );
}

function Toggle({ enabled, onToggle, label, field, icon }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, cursor: "pointer" }} onClick={onToggle}>
      <div style={{
        width: 38, height: 22, borderRadius: 11, border: "none", cursor: "pointer",
        background: enabled ? "var(--accent)" : "var(--border)", position: "relative", transition: "background .2s", flexShrink: 0
      }}>
        <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#fff", position: "absolute", top: 3, left: enabled ? 19 : 3, transition: "left .2s", boxShadow: "0 1px 4px rgba(0,0,0,.15)" }} />
      </div>
      {icon && <span style={{ color: enabled ? "var(--accent)" : "var(--muted)", transition: "color .2s" }}>{icon}</span>}
      <span style={{ fontSize: 12, color: enabled ? "var(--text)" : "var(--muted)", fontWeight: enabled ? 600 : 400, transition: "all .2s", letterSpacing: ".01em" }}>{label}</span>
      <InfoPopup field={field} />
    </div>
  );
}

function SectionLabel({ icon, label, field }) {
  return (
    <div style={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", gap: 8, margin: "8px 0 4px" }}>
      <div style={{ width: 28, height: 28, borderRadius: 8, background: "var(--accent-soft)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--accent)", flexShrink: 0 }}>
        {icon}
      </div>
      <span style={{ fontSize: 11, fontWeight: 700, color: "var(--accent)", letterSpacing: ".1em", textTransform: "uppercase" }}>{label}</span>
      {field && <InfoPopup field={field} />}
      <div style={{ flex: 1, height: 1, background: "var(--border)", marginLeft: 4 }} />
    </div>
  );
}

function MultiSelect({ label, field, options, selected, onChange, icons }) {
  const toggle = (val) => onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);
  return (
    <div>
      <div className="field-label">{label}<InfoPopup field={field} /></div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {options.map((opt, i) => {
          const active = selected.includes(opt);
          return (
            <button key={opt} onClick={() => toggle(opt)} style={{
              fontFamily: "var(--font-body)", fontSize: 12, padding: "8px 16px",
              borderRadius: 50, border: "1.5px solid " + (active ? "var(--accent)" : "var(--border)"),
              background: active ? "var(--accent)" : "var(--chip-bg)", color: active ? "#fff" : "var(--muted)",
              cursor: "pointer", transition: "all .15s", fontWeight: active ? 600 : 400,
              display: "flex", alignItems: "center", gap: 6,
            }}>
              {icons && icons[i] && <span>{icons[i]}</span>}
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CityInput({ value, onChange, onSelect, onSearch }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef(null);
  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setShowSuggestions(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const handleChange = (e) => {
    const val = e.target.value; onChange(val); setActiveIndex(-1);
    if (val.length >= 2) {
      const filtered = US_CITIES.filter(c => c.toLowerCase().includes(val.toLowerCase())).slice(0, 6);
      setSuggestions(filtered); setShowSuggestions(filtered.length > 0);
    } else { setSuggestions([]); setShowSuggestions(false); }
  };
  const handleSelect = (city) => { onSelect(city); setSuggestions([]); setShowSuggestions(false); setActiveIndex(-1); };
  const handleKeyDown = (e) => {
    if (!showSuggestions) { if (e.key === "Enter") onSearch(); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIndex(i => Math.min(i + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIndex(i => Math.max(i - 1, -1)); }
    else if (e.key === "Enter") { e.preventDefault(); if (activeIndex >= 0) handleSelect(suggestions[activeIndex]); else { setShowSuggestions(false); onSearch(); } }
    else if (e.key === "Escape") setShowSuggestions(false);
  };
  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      <div style={{ position: "relative" }}>
        <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--muted)", pointerEvents: "none" }}>
          <Icon name="pin" size={15} color="var(--accent)" />
        </span>
        <input className="txt-input" type="text" placeholder="e.g. Austin, TX" value={value}
          onChange={handleChange} onKeyDown={handleKeyDown}
          onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
          autoComplete="off" style={{ paddingLeft: 36 }} />
      </div>
      {showSuggestions && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100, background: "var(--surface)", border: "1.5px solid var(--border)", borderTop: "none", borderRadius: "0 0 12px 12px", boxShadow: "0 8px 24px rgba(108,71,255,.1)", overflow: "hidden" }}>
          {suggestions.map((city, i) => (
            <div key={city} onMouseDown={() => handleSelect(city)}
              style={{ padding: "10px 14px", fontSize: 12, fontFamily: "var(--font-body)", cursor: "pointer", color: i === activeIndex ? "#fff" : "var(--text)", background: i === activeIndex ? "var(--accent)" : "transparent", borderBottom: i < suggestions.length - 1 ? "1px solid var(--border)" : "none", display: "flex", alignItems: "center", gap: 8 }}
              onMouseEnter={() => setActiveIndex(i)}>
              <Icon name="pin" size={12} color={i === activeIndex ? "#fff" : "var(--muted)"} />
              {city}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function GymInput({ value, onChange }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const wrapperRef = useRef(null);
  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setShowSuggestions(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const handleChange = (e) => {
    const val = e.target.value; onChange(val);
    if (val.length >= 1) {
      const filtered = POPULAR_GYMS.filter(g => g.toLowerCase().includes(val.toLowerCase())).slice(0, 6);
      setSuggestions(filtered); setShowSuggestions(filtered.length > 0);
    } else { setSuggestions([]); setShowSuggestions(false); }
  };
  const handleSelect = (gym) => { onChange(gym); setSuggestions([]); setShowSuggestions(false); };
  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      <div style={{ position: "relative" }}>
        <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
          <Icon name="gym" size={15} color="var(--accent)" />
        </span>
        <input className="txt-input" type="text" placeholder="e.g. Planet Fitness, Equinox, YMCA..." value={value}
          onChange={handleChange} onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
          autoComplete="off" style={{ paddingLeft: 36 }} />
      </div>
      {showSuggestions && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100, background: "var(--surface)", border: "1.5px solid var(--border)", borderTop: "none", borderRadius: "0 0 12px 12px", boxShadow: "0 8px 24px rgba(108,71,255,.1)", overflow: "hidden" }}>
          {suggestions.map((gym, i) => (
            <div key={gym} onMouseDown={() => handleSelect(gym)}
              style={{ padding: "10px 14px", fontSize: 12, fontFamily: "var(--font-body)", cursor: "pointer", color: "var(--text)", background: "transparent", borderBottom: i < suggestions.length - 1 ? "1px solid var(--border)" : "none", display: "flex", alignItems: "center", gap: 8 }}
              onMouseEnter={e => e.currentTarget.style.background = "var(--accent-soft)"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <Icon name="gym" size={12} color="var(--accent)" />
              {gym}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ChildcareInput({ value, onChange, location }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setShowSuggestions(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    onChange(val);
    clearTimeout(debounceRef.current);
    if (val.length >= 2) {
      setLoading(true);
      debounceRef.current = setTimeout(async () => {
        try {
          const res = await fetch("/api/claude", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              type: "places",
              body: {
                location: location || "United States",
                radius: 50000,
                placeType: "child_care_agency",
                query: val + " daycare preschool childcare licensed",
              }
            }),
          });
          const data = await res.json();
          const EXCLUDED = ["bar","restaurant","café","cafe","coffee","tavern","grill","pub","brewery","lounge","club","hotel","salon","spa","gym","fitness","yoga","pizza","burger","diner","bistro","food","eatery","sushi","taco","bbq","wing"];
          const filtered = (data.results || []).filter(p => {
            const name = (p.name || "").toLowerCase();
            return !EXCLUDED.some(k => name.includes(k));
          }).slice(0, 7);
          setSuggestions(filtered);
          setShowSuggestions(filtered.length > 0);
        } catch (e) {
          setSuggestions([]);
        } finally {
          setLoading(false);
        }
      }, 350);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
      setLoading(false);
    }
  };

  const handleSelect = (place) => {
    onChange(place.name);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      <div style={{ position: "relative" }}>
        <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
          <Icon name="baby" size={15} color="var(--accent)" />
        </span>
        <input className="txt-input" type="text"
          placeholder="e.g. Bright Horizons, KinderCare, La Petite..."
          value={value} onChange={handleChange}
          onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
          autoComplete="off" style={{ paddingLeft: 36, paddingRight: loading ? 36 : 14 }} />
        {loading && (
          <span style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
            <div style={{ width: 14, height: 14, border: "2px solid var(--border)", borderTop: "2px solid var(--accent)", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
          </span>
        )}
      </div>
      {showSuggestions && suggestions.length > 0 && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 200, background: "var(--surface)", border: "1.5px solid var(--border)", borderTop: "none", borderRadius: "0 0 12px 12px", boxShadow: "0 8px 24px rgba(108,71,255,.12)", overflow: "hidden" }}>
          {suggestions.map((place, i) => (
            <div key={place.id || i} onMouseDown={() => handleSelect(place)}
              style={{ padding: "10px 14px", fontSize: 12, cursor: "pointer", borderBottom: i < suggestions.length - 1 ? "1px solid var(--border)" : "none", display: "flex", alignItems: "flex-start", gap: 8 }}
              onMouseEnter={e => e.currentTarget.style.background = "var(--accent-soft)"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <Icon name="baby" size={13} color="var(--accent)" style={{ marginTop: 2, flexShrink: 0 }} />
              <div>
                <div style={{ fontWeight: 600, color: "var(--text)" }}>{place.name}</div>
                <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 1 }}>{place.address}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

async function fetchListings({ city, state, beds, baths, listingTypes, housingTypes, maxRent, maxListPrice }) {
  const res = await fetch("/api/claude", {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ type: "listings", body: { city, state, bedrooms: beds, bathrooms: baths, listingTypes, housingTypes, maxRent, maxListPrice } }),
  });
  return res.json();
}

async function fetchAnalysis(prompt) {
  const res = await fetch("/api/claude", {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ type: "analysis", body: { model: "claude-sonnet-4-5", max_tokens: 2000, messages: [{ role: "user", content: prompt }] } }),
  });
  const data = await res.json();
  const text = data.content?.map(b => b.text || "").join("") || "";
  const clean = text.replace(/```json[\s\S]*?```/g, m => m.slice(7, -3)).replace(/```/g, "").trim();
  const jsonMatch = clean.match(/\{[\s\S]*\}/);
  if (!jsonMatch) throw new Error("No JSON in analysis response");
  return JSON.parse(jsonMatch[0]);
}

function slugify(str) {
  return (str || "").trim().toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
}

function parseAddress(formattedAddress) {
  if (!formattedAddress) return {};
  const parts = formattedAddress.split(",").map(s => s.trim());
  const street = parts[0] || "";
  const city = parts[1] || "";
  const stateZipRaw = parts[2] || "";
  const stateZipParts = stateZipRaw.trim().split(/\s+/);
  const state = stateZipParts[0] || "";
  const zip = stateZipParts[1] || "";
  return { street, city, state, zip, cityLower: slugify(city), stateLower: slugify(state), streetLower: slugify(street) };
}

function buildZillowUrl(raw) {
  if (!raw.formattedAddress) return "https://www.zillow.com/homes/" + (raw.zipCode || "") + "_rb/";
  const { street, city, state, zip } = parseAddress(raw.formattedAddress);
  const addressPart = [street, city, state, zip].filter(Boolean).join(", ").replace(/\s/g, "+");
  const type = raw.listingType === "for_sale" ? "for_sale" : "for_rent";
  return "https://www.zillow.com/homes/" + type + "/" + encodeURIComponent(addressPart) + "_rb/";
}

function buildApartmentsUrl(raw) {
  if (!raw.formattedAddress) return "https://www.apartments.com/" + (raw.zipCode || "") + "/";
  const { streetLower, cityLower, stateLower, zip } = parseAddress(raw.formattedAddress);
  if (streetLower && cityLower && stateLower) {
    return "https://www.apartments.com/" + cityLower + "-" + stateLower + (zip ? "-" + zip : "") + "/" + streetLower + "/";
  }
  return "https://www.apartments.com/" + (raw.zipCode || "") + "/";
}

function buildRealtorUrl(raw) {
  if (!raw.formattedAddress) return "https://www.realtor.com/realestateandhomes-search/" + (raw.zipCode || "");
  const { street, city, state, zip } = parseAddress(raw.formattedAddress);
  const streetSlug = (street || "").replace(/\s+/g, "-");
  if (raw.listingType === "for_sale") {
    return "https://www.realtor.com/realestateandhomes-detail/" +
      encodeURIComponent(streetSlug) + "_" +
      encodeURIComponent(city) + "_" +
      encodeURIComponent(state) + "_" +
      encodeURIComponent(zip);
  }
  return "https://www.realtor.com/apartments/" + encodeURIComponent(city) + "_" + encodeURIComponent(state) + "/";
}

function transformListing(raw, maxRent, maxListPrice, isForSale) {
  const price = raw.price || raw.rent || 0;
  const cap = isForSale ? maxListPrice : maxRent;
  const outsideFilters = price > cap;
  const score = outsideFilters ? Math.floor(60 + Math.random() * 15) : Math.floor(80 + Math.random() * 20);
  return {
    id: raw.id || raw.formattedAddress,
    score, status: "ACTIVE",
    listingType: raw.listingType === "for_sale" ? "Buy" : "Rent",
    housingType: raw.propertyType || "-",
    zip: raw.zipCode || "", neighborhood: raw.city || "",
    name: raw.formattedAddress ? raw.formattedAddress.split(",")[0] : "Listing",
    address: raw.formattedAddress || "",
    lat: raw.latitude, lng: raw.longitude,
    rent: price, fees: 0,
    beds: raw.bedrooms ? raw.bedrooms + "BR" : "-",
    sqft: raw.squareFootage, outsideFilters,
    reasons: outsideFilters ? ["$" + price.toLocaleString() + " > $" + cap.toLocaleString() + " cap"] : [],
    metrics: [
      { label: "Beds", value: raw.bedrooms || "-", highlight: false },
      { label: "Baths", value: raw.bathrooms || "-", highlight: false },
      { label: "Sqft", value: raw.squareFootage ? raw.squareFootage.toLocaleString() : "-", highlight: false },
      { label: "Year Built", value: raw.yearBuilt || "-", highlight: false },
    ],
    zillowUrl: buildZillowUrl(raw),
    aptUrl: buildApartmentsUrl(raw),
    realtorUrl: buildRealtorUrl(raw),
  };
}

function ScoreRing({ score }) {
  const r = 22, circ = 2 * Math.PI * r;
  const color = score >= 90 ? "var(--accent)" : score >= 80 ? "#f0b429" : "#e05050";
  return (
    <div style={{ width: 54, height: 54, position: "relative", flexShrink: 0 }}>
      <svg width="54" height="54" style={{ transform: "rotate(-90deg)" }}>
        <circle cx="27" cy="27" r={r} fill="none" stroke="var(--border)" strokeWidth="4" />
        <circle cx="27" cy="27" r={r} fill="none" stroke={color} strokeWidth="4" strokeDasharray={(score / 100) * circ + " " + circ} strokeLinecap="round" />
      </svg>
      <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", fontSize: 13, fontWeight: 700, fontFamily: "var(--font-display)", color }}>{score}</div>
    </div>
  );
}

function MetaItem({ label, value, icon }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", background: "var(--chip-bg)", borderRadius: 10, padding: "8px 12px", minWidth: 60 }}>
      {icon && <span style={{ color: "var(--accent)", marginBottom: 3 }}>{icon}</span>}
      <span style={{ fontSize: 9, letterSpacing: ".1em", color: "var(--muted)", textTransform: "uppercase", fontFamily: "var(--font-body)", marginBottom: 2 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)", fontFamily: "var(--font-display)" }}>{value}</span>
    </div>
  );
}

function ListingCard({ listing, cap, dimmed }) {
  const underCap = cap - listing.rent;
  const isBuy = listing.listingType === "Buy";
  return (
    <div style={{
      background: "var(--surface)", border: "1.5px solid var(--border)",
      borderRadius: 18, padding: "20px", marginBottom: 14,
      display: "flex", gap: 16, alignItems: "flex-start",
      opacity: dimmed ? 0.5 : 1,
      boxShadow: dimmed ? "none" : "var(--shadow-card)",
      transition: "all .2s",
    }}
      onMouseEnter={e => { if (!dimmed) e.currentTarget.style.boxShadow = "var(--shadow-card-hover)"; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = dimmed ? "none" : "var(--shadow-card)"; }}
    >
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
        <ScoreRing score={listing.score} />
        <span style={{ fontSize: 8, color: "var(--muted)", letterSpacing: ".08em", textTransform: "uppercase" }}>match</span>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
          <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 20, background: "#e8faf0", color: "#1db96a" }}>● ACTIVE</span>
          {listing.listingType && <span style={{ fontSize: 10, padding: "3px 8px", borderRadius: 20, background: isBuy ? "var(--accent-soft)" : "var(--chip-bg)", color: isBuy ? "var(--accent)" : "var(--muted)", fontWeight: 600 }}>{listing.listingType}</span>}
          {listing.housingType && <span style={{ fontSize: 10, padding: "3px 8px", borderRadius: 20, background: "var(--chip-bg)", color: "var(--muted)" }}>{listing.housingType}</span>}
        </div>
        <div style={{ fontSize: 17, fontWeight: 700, fontFamily: "var(--font-display)", color: "var(--text)", lineHeight: 1.2 }}>{listing.name}</div>
        <div style={{ fontSize: 11, color: "var(--accent)", fontFamily: "var(--font-body)", marginTop: 2, display: "flex", alignItems: "center", gap: 4 }}>
          <Icon name="pin" size={11} color="var(--accent)" />
          {listing.neighborhood} {listing.zip}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
          {listing.metrics && listing.metrics.map(m => (
            <MetaItem key={m.label} label={m.label} value={m.value}
              icon={m.label === "Beds" ? <Icon name="bed" size={12} /> : m.label === "Baths" ? <Icon name="bath" size={12} /> : null}
            />
          ))}
        </div>
        {dimmed && listing.reasons && listing.reasons.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 10 }}>
            {listing.reasons.map(r => <span key={r} style={{ fontSize: 9, color: "#e05050", border: "1px solid #fde0e0", background: "#fff9f9", borderRadius: 20, padding: "2px 10px" }}>{r}</span>)}
          </div>
        )}
        <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
          <a href={listing.zillowUrl} target="_blank" rel="noreferrer" className="pill-btn pill-btn-primary" title={"Search " + listing.address + " on Zillow"}>
            <Icon name="external" size={11} color="#fff" /> Zillow
          </a>
          {!listing.listingType || listing.listingType === "Rent" ? (
            <a href={listing.aptUrl} target="_blank" rel="noreferrer" className="pill-btn pill-btn-danger" title={"Search " + listing.address + " on Apartments.com"}>
              <Icon name="external" size={11} color="#fff" /> Apartments.com
            </a>
          ) : null}
          <a href={listing.realtorUrl} target="_blank" rel="noreferrer" className="pill-btn" style={{ background: "#d4202a", color: "#fff" }} title={"Search " + listing.address + " on Realtor.com"}>
            <Icon name="external" size={11} color="#fff" /> Realtor.com
          </a>
        </div>
      </div>
      <div style={{ textAlign: "right", flexShrink: 0 }}>
        <div style={{ fontSize: 22, fontWeight: 800, fontFamily: "var(--font-display)", color: "var(--text)" }}>
          ${listing.rent ? listing.rent.toLocaleString() : "0"}
          {!isBuy && <span style={{ fontSize: 13, color: "var(--muted)", fontWeight: 400 }}>/mo</span>}
        </div>
        {isBuy && <div style={{ fontSize: 10, color: "var(--accent)", marginTop: 2, fontWeight: 600 }}>list price</div>}
        {!dimmed && underCap > 0 && <div style={{ fontSize: 10, color: "#1db96a", marginTop: 4, fontWeight: 600 }}>${underCap.toLocaleString()} under cap</div>}
      </div>
    </div>
  );
}

function NeighborhoodMap({ listings }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  useEffect(() => {
    if (!mapRef.current || !listings?.length) return;
    function initMap() {
      const L = window.L;
      if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; }
      const withCoords = listings.filter(l => l.lat && l.lng);
      if (!withCoords.length) return;
      const centerLat = withCoords.reduce((s, l) => s + l.lat, 0) / withCoords.length;
      const centerLng = withCoords.reduce((s, l) => s + l.lng, 0) / withCoords.length;
      const map = L.map(mapRef.current, { zoomControl: true, scrollWheelZoom: false }).setView([centerLat, centerLng], 12);
      mapInstanceRef.current = map;
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "OpenStreetMap" }).addTo(map);
      withCoords.forEach(listing => {
        const qualified = !listing.outsideFilters;
        const color = qualified ? "#6c47ff" : "#ddd";
        const icon = L.divIcon({
          html: '<div style="width:36px;height:36px;border-radius:50%;background:' + color + ';border:3px solid ' + (qualified ? "#fff" : "#bbb") + ';display:flex;align-items:center;justify-content:center;font-family:monospace;font-size:11px;font-weight:700;color:#fff;box-shadow:0 4px 12px rgba(108,71,255,.3)">' + listing.score + '</div>',
          className: "", iconSize: [36, 36], iconAnchor: [18, 18],
        });
        const marker = L.marker([listing.lat, listing.lng], { icon }).addTo(map);
        marker.bindPopup(
          '<div style="font-family:sans-serif;font-size:12px;min-width:200px;padding:4px">' +
          '<div style="font-weight:700;font-size:14px;margin-bottom:4px">' + listing.name + '</div>' +
          '<div style="color:#888;margin-bottom:4px;font-size:11px">' + listing.address + '</div>' +
          '<div style="color:#6c47ff;font-weight:700;font-size:16px;margin-bottom:6px">$' + (listing.rent ? listing.rent.toLocaleString() : "0") + (listing.listingType === "Buy" ? "" : "/mo") + '</div>' +
          (qualified ? '<div style="color:#1db96a;font-weight:600;margin-bottom:8px">✓ Matches filters</div>' : '<div style="color:#e05050;margin-bottom:8px">Outside filters</div>') +
          '<div style="display:flex;gap:6px;flex-wrap:wrap">' +
          '<a href="' + listing.zillowUrl + '" target="_blank" style="color:#fff;background:#006aff;padding:4px 10px;border-radius:12px;font-size:10px;font-weight:600;text-decoration:none">Zillow</a>' +
          (listing.listingType !== "Buy" ? '<a href="' + listing.aptUrl + '" target="_blank" style="color:#fff;background:#e84545;padding:4px 10px;border-radius:12px;font-size:10px;font-weight:600;text-decoration:none">Apts.com</a>' : '') +
          '<a href="' + listing.realtorUrl + '" target="_blank" style="color:#fff;background:#d4202a;padding:4px 10px;border-radius:12px;font-size:10px;font-weight:600;text-decoration:none">Realtor</a>' +
          '</div>' +
          '</div>'
        );
      });
    }
    if (!window.L) {
      const link = document.createElement("link"); link.rel = "stylesheet"; link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"; document.head.appendChild(link);
      const script = document.createElement("script"); script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"; script.onload = initMap; document.head.appendChild(script);
    } else { initMap(); }
    return () => { if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; } };
  }, [listings]);
  return (
    <div style={{ borderRadius: 18, overflow: "hidden", border: "1.5px solid var(--border)", marginBottom: 20, boxShadow: "var(--shadow-card)" }}>
      <div style={{ background: "var(--surface)", padding: "16px 20px 12px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontSize: 10, letterSpacing: ".12em", color: "var(--muted)", textTransform: "uppercase", marginBottom: 2 }}>Neighborhood Map</div>
          <div style={{ display: "flex", gap: 14, fontSize: 11 }}>
            <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: "var(--accent)" }} /> Matches filters
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: "#ddd", border: "1px solid #bbb" }} /> Outside filters
            </span>
          </div>
        </div>
        <Icon name="map" size={20} color="var(--accent)" />
      </div>
      <div ref={mapRef} style={{ height: 400, width: "100%" }} />
    </div>
  );
}

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={{ background: "var(--surface-dark)", color: "#fff", padding: "10px 14px", borderRadius: 10, fontSize: 12, fontFamily: "var(--font-body)", boxShadow: "0 4px 20px rgba(0,0,0,.2)" }}>
      <div style={{ color: "rgba(255,255,255,.6)", marginBottom: 3 }}>{label}</div>
      <div style={{ color: "var(--accent-light)", fontWeight: 700 }}>${payload[0].value ? payload[0].value.toLocaleString() : "0"}/mo</div>
    </div>
  );
};

function Spinner({ message }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 20px", gap: 16 }}>
      <div style={{ width: 40, height: 40, border: "3px solid var(--border)", borderTop: "3px solid var(--accent)", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      <div style={{ fontSize: 11, color: "var(--muted)", letterSpacing: ".12em", textTransform: "uppercase" }}>{message || "Loading..."}</div>
      <style>{"@keyframes spin { to { transform: rotate(360deg); } }"}</style>
    </div>
  );
}

function ScrollToTop() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  if (!visible) return null;
  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      style={{
        position: "fixed", bottom: 24, right: 24, zIndex: 999,
        width: 44, height: 44, borderRadius: "50%",
        background: "var(--accent)", border: "none", cursor: "pointer",
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 4px 16px rgba(108,71,255,.4)", transition: "all .2s",
      }}
      onMouseEnter={e => e.currentTarget.style.transform = "translateY(-2px)"}
      onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}
      title="Scroll to top"
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="18 15 12 9 6 15"/>
      </svg>
    </button>
  );
}

function Pagination({ pageSize, setPageSize, page, setPage, total }) {
  const totalPages = Math.ceil(total / pageSize);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginRight: 8 }}>
        <span style={{ fontSize: 11, color: "var(--muted)", whiteSpace: "nowrap" }}>Per page:</span>
        {[10, 20, 40].map(n => (
          <button key={n} onClick={() => { setPageSize(n); setPage(1); }} style={{
            fontFamily: "var(--font-body)", fontSize: 11, padding: "5px 11px", borderRadius: 20,
            border: "1.5px solid " + (pageSize === n ? "var(--accent)" : "var(--border)"),
            background: pageSize === n ? "var(--accent)" : "var(--chip-bg)",
            color: pageSize === n ? "#fff" : "var(--muted)",
            cursor: "pointer", fontWeight: 600, transition: "all .15s",
          }}>{n}</button>
        ))}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: "auto" }}>
        <button onClick={() => { setPage(p => Math.max(1, p - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          disabled={page === 1} style={{
            fontFamily: "var(--font-body)", fontSize: 11, padding: "5px 14px", borderRadius: 20,
            border: "1.5px solid var(--border)", background: "var(--chip-bg)",
            color: page === 1 ? "var(--border)" : "var(--text)", cursor: page === 1 ? "default" : "pointer",
            fontWeight: 600, display: "flex", alignItems: "center", gap: 4, transition: "all .15s",
          }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          Prev
        </button>
        <span style={{ fontSize: 11, color: "var(--muted)", whiteSpace: "nowrap" }}>
          Page {page} of {totalPages} · {total} results
        </span>
        <button onClick={() => { setPage(p => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          disabled={page === totalPages || totalPages === 0} style={{
            fontFamily: "var(--font-body)", fontSize: 11, padding: "5px 14px", borderRadius: 20,
            border: "1.5px solid var(--border)", background: "var(--chip-bg)",
            color: (page === totalPages || totalPages === 0) ? "var(--border)" : "var(--text)",
            cursor: (page === totalPages || totalPages === 0) ? "default" : "pointer",
            fontWeight: 600, display: "flex", alignItems: "center", gap: 4, transition: "all .15s",
          }}>
          Next
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
        </button>
      </div>
    </div>
  );
}

function StatPill({ label, value }) {
  return (
    <div style={{ background: "var(--surface)", border: "1.5px solid var(--border)", borderRadius: 14, padding: "10px 18px", display: "flex", flexDirection: "column", alignItems: "center", minWidth: 90, boxShadow: "var(--shadow-card)" }}>
      <span style={{ fontSize: 9, letterSpacing: ".1em", color: "var(--muted)", textTransform: "uppercase", marginBottom: 3 }}>{label}</span>
      <span style={{ fontSize: 15, fontWeight: 800, color: "var(--accent)", fontFamily: "var(--font-display)" }}>{value}</span>
    </div>
  );
}

function parseHomeParams() {
  try {
    const p = new URLSearchParams(window.location.search);
    return {
      cityDraft:         p.get("city") || "",
      movingDate:        p.get("date") || "January 2027",
      beds:              p.get("beds") ? p.get("beds").split(",") : [],
      baths:             p.get("baths") ? p.get("baths").split(",") : [],
      listingTypes:      p.get("types") ? p.get("types").split(",") : [],
      housingTypes:      p.get("housing") ? p.get("housing").split(",") : [],
      maxRent:           p.get("rent") ? +p.get("rent") : 2200,
      maxListPrice:      p.get("price") ? +p.get("price") : 400000,
      extraFilters:      p.get("extra") || "",
      commuteEnabled:    p.get("commute") === "1",
      commuteAddress:    p.get("caddr") || "",
      commuteMax:        p.get("cmax") ? +p.get("cmax") : 30,
      groceryEnabled:    p.get("grocery") === "1",
      maxDrive:          p.get("gdrive") ? +p.get("gdrive") : 7,
      daycareEnabled:    p.get("daycare") === "1",
      daycareMax:        p.get("dmax") ? +p.get("dmax") : 10,
      preferredChildcare: p.get("childcare") || "",
      gymEnabled:        p.get("gym") === "1",
      gymMax:            p.get("gmax") ? +p.get("gmax") : 10,
      preferredGym:      p.get("gymname") || "",
      crimeEnabled:      p.get("crime") === "1",
      crimeFilter:       p.get("crimelvl") || "Bottom 20%",
      schoolEnabled:     p.get("school") === "1",
      schoolFilter:      p.get("schoollvl") || "Top 10%",
    };
  } catch { return {}; }
}

function HomeSearch({ onShareUrl }) {
  const init = parseHomeParams();
  const [city, setCity] = useState("");
  const [cityDraft, setCityDraft] = useState(init.cityDraft || "");
  const [movingDate, setMovingDate] = useState(init.movingDate || "January 2027");
  const [beds, setBeds] = useState(init.beds || []);
  const [baths, setBaths] = useState(init.baths || []);
  const [listingTypes, setListingTypes] = useState(init.listingTypes || []);
  const [housingTypes, setHousingTypes] = useState(init.housingTypes || []);
  const [maxRent, setMaxRent] = useState(init.maxRent ?? 2200);
  const [maxListPrice, setMaxListPrice] = useState(init.maxListPrice ?? 400000);
  const [extraFilters, setExtraFilters] = useState(init.extraFilters || "");
  const [commuteEnabled, setCommuteEnabled] = useState(init.commuteEnabled || false);
  const [commuteAddress, setCommuteAddress] = useState(init.commuteAddress || "");
  const [commuteMax, setCommuteMax] = useState(init.commuteMax ?? 30);
  const [groceryEnabled, setGroceryEnabled] = useState(init.groceryEnabled || false);
  const [maxDrive, setMaxDrive] = useState(init.maxDrive ?? 7);
  const [daycareEnabled, setDaycareEnabled] = useState(init.daycareEnabled || false);
  const [daycareMax, setDaycareMax] = useState(init.daycareMax ?? 10);
  const [preferredChildcare, setPreferredChildcare] = useState(init.preferredChildcare || "");
  const [gymEnabled, setGymEnabled] = useState(init.gymEnabled || false);
  const [gymMax, setGymMax] = useState(init.gymMax ?? 10);
  const [preferredGym, setPreferredGym] = useState(init.preferredGym || "");
  const [crimeEnabled, setCrimeEnabled] = useState(init.crimeEnabled || false);
  const [crimeFilter, setCrimeFilter] = useState(init.crimeFilter || "Bottom 20%");
  const [schoolEnabled, setSchoolEnabled] = useState(init.schoolEnabled || false);
  const [schoolFilter, setSchoolFilter] = useState(init.schoolFilter || "Top 10%");
  const [listings, setListings] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [loadingListings, setLoadingListings] = useState(false);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!onShareUrl) return;
    const p = new URLSearchParams();
    if (cityDraft)          p.set("city", cityDraft);
    if (movingDate !== "January 2027") p.set("date", movingDate);
    if (beds.length)        p.set("beds", beds.join(","));
    if (baths.length)       p.set("baths", baths.join(","));
    if (listingTypes.length) p.set("types", listingTypes.join(","));
    if (housingTypes.length) p.set("housing", housingTypes.join(","));
    if (maxRent !== 2200)   p.set("rent", maxRent);
    if (maxListPrice !== 400000) p.set("price", maxListPrice);
    if (extraFilters)       p.set("extra", extraFilters);
    if (commuteEnabled)     { p.set("commute", "1"); p.set("cmax", commuteMax); if (commuteAddress) p.set("caddr", commuteAddress); }
    if (groceryEnabled)     { p.set("grocery", "1"); p.set("gdrive", maxDrive); }
    if (daycareEnabled)     { p.set("daycare", "1"); p.set("dmax", daycareMax); if (preferredChildcare) p.set("childcare", preferredChildcare); }
    if (gymEnabled)         { p.set("gym", "1"); p.set("gmax", gymMax); if (preferredGym) p.set("gymname", preferredGym); }
    if (crimeEnabled)       { p.set("crime", "1"); p.set("crimelvl", crimeFilter); }
    if (schoolEnabled)      { p.set("school", "1"); p.set("schoollvl", schoolFilter); }
    const qs = p.toString();
    onShareUrl("https://movemtrc.com/" + (qs ? "?" + qs : "") + "#homes");
  }, [onShareUrl, cityDraft, movingDate, beds, baths, listingTypes, housingTypes, maxRent, maxListPrice, extraFilters,
      commuteEnabled, commuteAddress, commuteMax, groceryEnabled, maxDrive,
      daycareEnabled, daycareMax, preferredChildcare, gymEnabled, gymMax, preferredGym,
      crimeEnabled, crimeFilter, schoolEnabled, schoolFilter]);

  const isForSale = listingTypes.includes("Buy") && !listingTypes.includes("Rent");
  const isBothTypes = listingTypes.includes("Buy") && listingTypes.includes("Rent");

  const didAutoSearch = useRef(false);
  useEffect(() => {
    if (!didAutoSearch.current && init.cityDraft && init.cityDraft.includes(",")) {
      didAutoSearch.current = true;
      setTimeout(() => handleSearch(), 100);
    }
  }, []); // eslint-disable-line

  const handleSearch = useCallback(async () => {
    if (!cityDraft.trim()) { setError("Please enter a city to search."); return; }
    const parts = cityDraft.trim().split(",");
    const cityName = parts[0] ? parts[0].trim() : "";
    const state = parts[1] ? parts[1].trim() : "";
    if (!state) { setError("Please include state abbreviation, e.g. Austin, TX"); return; }
    setError(null); setHasSearched(true); setCity(cityDraft.trim()); setListings([]); setAnalysis(null); setPage(1);
    setLoadingListings(true);
    try {
      const raw = await fetchListings({ city: cityName, state, beds, baths, listingTypes, housingTypes, maxRent, maxListPrice });
      const transformed = (Array.isArray(raw) ? raw : raw.listings || []).map(r => transformListing(r, maxRent, maxListPrice, isForSale));
      setListings(transformed);
    } catch (e) {
      setError("Failed to load listings. Please try again.");
    } finally {
      setLoadingListings(false);
    }
    setLoadingAnalysis(true);
    try {
      const lifestyleLines = [
        commuteEnabled && commuteAddress ? "- Max commute to " + commuteAddress + ": " + commuteMax + " min" : null,
        groceryEnabled ? "- Max drive to premium grocery: " + maxDrive + " min" : null,
        daycareEnabled ? "- Max drive to daycare" + (preferredChildcare ? " (" + preferredChildcare + ")" : "") + ": " + daycareMax + " min" : null,
        gymEnabled ? "- Max drive to gym" + (preferredGym ? " (" + preferredGym + ")" : "") + ": " + gymMax + " min" : null,
        crimeEnabled ? "- Crime threshold: " + crimeFilter : null,
        schoolEnabled ? "- School district: " + schoolFilter + " or better" : null,
      ].filter(Boolean).join("\n");
      const prompt = "Generate a rent trend analysis for " + cityDraft.trim() + " moving around " + movingDate + ". Budget: max $" + maxRent + "/mo rent" + (isBothTypes || isForSale ? ", max $" + maxListPrice.toLocaleString() + " list price" : "") + (lifestyleLines ? "\n" + lifestyleLines : "") + ". Return ONLY valid JSON: { \"summary\": { \"projectedRent\": 2000 }, \"rentTrend\": [ { \"month\": \"Jan 25\", \"rent\": 2050 }, { \"month\": \"Mar 25\", \"rent\": 2080 }, { \"month\": \"May 25\", \"rent\": 2160 }, { \"month\": \"Jul 25\", \"rent\": 2200 }, { \"month\": \"Sep 25\", \"rent\": 2130 }, { \"month\": \"Nov 25\", \"rent\": 2060 }, { \"month\": \"Jan 26\", \"rent\": 2020 }, { \"month\": \"Mar 26\", \"rent\": 2010 }, { \"month\": \"May 26\", \"rent\": 2000 } ], \"neighborhoodInsight\": \"2-3 sentence market summary\" }";
      const result = await fetchAnalysis(prompt);
      setAnalysis(result);
    } catch (e) {
      console.error("Analysis failed:", e);
    } finally {
      setLoadingAnalysis(false);
    }
  }, [cityDraft, movingDate, beds, baths, listingTypes, housingTypes, maxRent, maxListPrice, isForSale, isBothTypes, commuteEnabled, commuteAddress, commuteMax, groceryEnabled, maxDrive, daycareEnabled, daycareMax, preferredChildcare, gymEnabled, gymMax, preferredGym, crimeEnabled, crimeFilter, schoolEnabled, schoolFilter]);

  const inside = listings.filter(l => !l.outsideFilters);
  const outside = listings.filter(l => l.outsideFilters);
  const allResults = [...inside, ...outside];
  const rentMin = analysis && analysis.rentTrend ? Math.min(...analysis.rentTrend.map(d => d.rent)) - 100 : 1800;
  const rentMax = analysis && analysis.rentTrend ? Math.max(...analysis.rentTrend.map(d => d.rent)) + 150 : 2400;

  const pageStart = (page - 1) * pageSize;
  const pageEnd = pageStart + pageSize;
  const pageResults = allResults.slice(pageStart, pageEnd);
  const visibleInside = pageResults.filter(l => !l.outsideFilters);
  const visibleOutside = pageResults.filter(l => l.outsideFilters);

  return (
    <div>
      <div style={{ background: "linear-gradient(135deg, #f8f6ff 0%, #f0ebff 50%, #e8f4ff 100%)", borderBottom: "1px solid var(--border)", padding: "32px 32px 28px" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
            <div style={{ width: 42, height: 42, borderRadius: 14, background: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 16px rgba(108,71,255,.3)" }}>
              <Icon name="home" size={20} color="#fff" />
            </div>
            <div>
              <div style={{ fontSize: 10, letterSpacing: ".2em", color: "var(--accent)", fontWeight: 700, textTransform: "uppercase" }}>
                {city ? city.toUpperCase() : "FIND YOUR NEXT HOME"}
              </div>
              <div style={{ fontSize: 28, fontWeight: 800, fontFamily: "var(--font-display)", color: "var(--text)", lineHeight: 1.1 }}>Home Search</div>
            </div>
          </div>
          <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 4, display: "flex", alignItems: "center", gap: 6 }}>
            Real listings from RentCast plus AI market analysis
            <span style={{ color: "var(--accent)" }}><Icon name="sparkle" size={14} color="var(--accent)" /></span>
          </div>
          {hasSearched && !loadingListings && (
            <div style={{ display: "flex", gap: 10, marginTop: 20, flexWrap: "wrap" }}>
              <StatPill label="Total" value={listings.length} />
              <StatPill label="Matching" value={inside.length} />
              <StatPill label="Outside" value={outside.length} />
              {!isForSale && <StatPill label="Max Rent" value={"$" + maxRent.toLocaleString()} />}
              {(isForSale || isBothTypes) && <StatPill label="Max Price" value={"$" + (maxListPrice / 1000).toFixed(0) + "k"} />}
            </div>
          )}
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: "0 auto", padding: "26px 24px" }}>
        <div className="card">
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 22 }}>
            <div style={{ width: 32, height: 32, borderRadius: 10, background: "var(--accent-soft)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Icon name="settings" size={16} color="var(--accent)" />
            </div>
            <span style={{ fontSize: 14, fontWeight: 700, color: "var(--text)", fontFamily: "var(--font-display)" }}>Your Search Criteria</span>
            <span style={{ color: "var(--accent)", marginLeft: 2 }}><Icon name="sparkle" size={14} color="var(--accent)" /></span>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px 28px" }}>
            <SectionLabel icon={<Icon name="map" size={14} />} label="Location & Date" />
            <div>
              <div className="field-label">City / Market<InfoPopup field="city" /></div>
              <CityInput value={cityDraft} onChange={setCityDraft} onSelect={setCityDraft} onSearch={handleSearch} />
            </div>
            <div>
              <div className="field-label">Moving Date<InfoPopup field="movingDate" /></div>
              <div style={{ position: "relative" }}>
                <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}><Icon name="calendar" size={15} color="var(--accent)" /></span>
                <input className="txt-input" type="text" placeholder="e.g. January 2027" value={movingDate} onChange={e => setMovingDate(e.target.value)} style={{ paddingLeft: 36 }} />
              </div>
            </div>
            <SectionLabel icon={<Icon name="bed" size={14} />} label="Bedrooms" field="beds" />
            <div style={{ gridColumn: "1 / -1" }}>
              <MultiSelect label="" options={["Studio", "1+", "2+", "3+", "4+"]} selected={beds} onChange={setBeds} />
            </div>
            <SectionLabel icon={<Icon name="bath" size={14} />} label="Bathrooms" field="baths" />
            <div style={{ gridColumn: "1 / -1" }}>
              <MultiSelect label="" options={["1+", "1.5+", "2+", "3+"]} selected={baths} onChange={setBaths} />
            </div>
            <SectionLabel icon={<Icon name="home" size={14} />} label="Rent or Buy" field="listingType" />
            <div style={{ gridColumn: "1 / -1" }}>
              <MultiSelect label="" options={["Rent", "Buy"]} selected={listingTypes} onChange={setListingTypes} />
              {listingTypes.includes("Rent") && (
                <div style={{ marginTop: 16, background: "var(--chip-bg)", borderRadius: 14, padding: "16px" }}>
                  <div className="field-label">Max Monthly Rent (all-in)<InfoPopup field="maxRent" /></div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: "var(--accent)", fontFamily: "var(--font-display)", marginBottom: 10 }}>${maxRent.toLocaleString()}<span style={{ fontSize: 13, fontWeight: 400, color: "var(--muted)" }}>/mo</span></div>
                  <input type="range" min={800} max={6000} step={50} value={maxRent} onChange={e => setMaxRent(+e.target.value)} />
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "var(--muted)", marginTop: 4 }}><span>$800</span><span>$6,000</span></div>
                </div>
              )}
              {listingTypes.includes("Buy") && (
                <div style={{ marginTop: 16, background: "var(--chip-bg)", borderRadius: 14, padding: "16px" }}>
                  <div className="field-label">Max List Price<InfoPopup field="maxListPrice" /></div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: "var(--accent)", fontFamily: "var(--font-display)", marginBottom: 10 }}>${maxListPrice.toLocaleString()}</div>
                  <input type="range" min={50000} max={2000000} step={10000} value={maxListPrice} onChange={e => setMaxListPrice(+e.target.value)} />
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "var(--muted)", marginTop: 4 }}><span>$50k</span><span>$2M</span></div>
                </div>
              )}
            </div>
            <SectionLabel icon={<Icon name="home" size={14} />} label="Housing Type" field="housingType" />
            <div style={{ gridColumn: "1 / -1" }}>
              <MultiSelect label="" options={["Apartment", "Condo", "House", "Townhouse", "Studio", "Duplex"]} selected={housingTypes} onChange={setHousingTypes} />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <div className="field-label">Additional Requirements (optional)<InfoPopup field="extra" /></div>
              <input className="txt-input" type="text" placeholder="e.g. dog park, EV charging, pool, in-unit laundry" value={extraFilters} onChange={e => setExtraFilters(e.target.value)} />
            </div>
            <SectionLabel icon={<Icon name="settings" size={14} />} label="Lifestyle Filters — toggle to enable" />
            <div style={{ gridColumn: "1 / -1" }}>
              <Toggle enabled={commuteEnabled} onToggle={() => setCommuteEnabled(v => !v)} label="Commute to Work" field="commute" icon={<Icon name="car" size={16} />} />
              {commuteEnabled && (
                <div style={{ paddingLeft: 48, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px", background: "var(--chip-bg)", borderRadius: 14, padding: "16px 16px 16px 48px", marginTop: 4 }}>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <div className="field-label">Work Address</div>
                    <div style={{ position: "relative" }}>
                      <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}><Icon name="pin" size={15} color="var(--accent)" /></span>
                      <input className="txt-input" type="text" placeholder="e.g. 100 Main St, Austin, TX" value={commuteAddress} onChange={e => setCommuteAddress(e.target.value)} style={{ paddingLeft: 36 }} />
                    </div>
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <div className="field-label">Max Commute Time</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: "var(--accent)", fontFamily: "var(--font-display)", marginBottom: 8 }}>{commuteMax} min</div>
                    <input type="range" min={5} max={90} step={5} value={commuteMax} onChange={e => setCommuteMax(+e.target.value)} />
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "var(--muted)", marginTop: 3 }}><span>5 min</span><span>90 min</span></div>
                  </div>
                </div>
              )}
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <Toggle enabled={groceryEnabled} onToggle={() => setGroceryEnabled(v => !v)} label="Max Drive to Premium Grocery" field="grocery" icon={<Icon name="grocery" size={16} />} />
              {groceryEnabled && (
                <div style={{ background: "var(--chip-bg)", borderRadius: 14, padding: "14px 16px", marginTop: 4, marginLeft: 48 }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: "var(--accent)", fontFamily: "var(--font-display)", marginBottom: 8 }}>{maxDrive} min</div>
                  <input type="range" min={2} max={30} step={1} value={maxDrive} onChange={e => setMaxDrive(+e.target.value)} />
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "var(--muted)", marginTop: 3 }}><span>2 min</span><span>30 min</span></div>
                </div>
              )}
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <Toggle enabled={daycareEnabled} onToggle={() => setDaycareEnabled(v => !v)} label="Max Drive to Daycare" field="daycare" icon={<Icon name="baby" size={16} />} />
              {daycareEnabled && (
                <div style={{ background: "var(--chip-bg)", borderRadius: 14, padding: "14px 16px", marginTop: 4, marginLeft: 48, display: "grid", gridTemplateColumns: "1fr", gap: 14 }}>
                  <div>
                    <div className="field-label">Specific Childcare (optional)</div>
                    <ChildcareInput value={preferredChildcare} onChange={setPreferredChildcare} location={cityDraft} />
                    {preferredChildcare && (
                      <div style={{ marginTop: 6, fontSize: 11, color: "var(--accent)", display: "flex", alignItems: "center", gap: 4 }}>
                        <Icon name="target" size={12} color="var(--accent)" />
                        Listings within drive-time of this childcare will be prioritized
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="field-label">Max Drive Time</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: "var(--accent)", fontFamily: "var(--font-display)", marginBottom: 8 }}>{daycareMax} min</div>
                    <input type="range" min={2} max={30} step={1} value={daycareMax} onChange={e => setDaycareMax(+e.target.value)} />
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "var(--muted)", marginTop: 3 }}><span>2 min</span><span>30 min</span></div>
                  </div>
                </div>
              )}
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <Toggle enabled={gymEnabled} onToggle={() => setGymEnabled(v => !v)} label="Max Drive to Gym" field="gym" icon={<Icon name="gym" size={16} />} />
              {gymEnabled && (
                <div style={{ background: "var(--chip-bg)", borderRadius: 14, padding: "14px 16px", marginTop: 4, marginLeft: 48, display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
                  <div>
                    <div className="field-label">Preferred Gym (optional)</div>
                    <GymInput value={preferredGym} onChange={setPreferredGym} />
                  </div>
                  <div>
                    <div className="field-label">Max Drive Time</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: "var(--accent)", fontFamily: "var(--font-display)", marginBottom: 8 }}>{gymMax} min</div>
                    <input type="range" min={2} max={30} step={1} value={gymMax} onChange={e => setGymMax(+e.target.value)} />
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "var(--muted)", marginTop: 3 }}><span>2 min</span><span>30 min</span></div>
                  </div>
                </div>
              )}
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <Toggle enabled={crimeEnabled} onToggle={() => setCrimeEnabled(v => !v)} label="Crime Threshold" field="crime" icon={<Icon name="shield" size={16} />} />
              {crimeEnabled && (
                <div style={{ paddingLeft: 48, marginTop: 4 }}>
                  <select value={crimeFilter} onChange={e => setCrimeFilter(e.target.value)} className="txt-input" style={{ width: "auto", minWidth: 200 }}>
                    {["Bottom 10%", "Bottom 20%", "Bottom 30%", "Bottom 40%", "Bottom 50%"].map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
              )}
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <Toggle enabled={schoolEnabled} onToggle={() => setSchoolEnabled(v => !v)} label="Min School District Rating" field="schools" icon={<Icon name="school" size={16} />} />
              {schoolEnabled && (
                <div style={{ paddingLeft: 48, marginTop: 4 }}>
                  <select value={schoolFilter} onChange={e => setSchoolFilter(e.target.value)} className="txt-input" style={{ width: "auto", minWidth: 200 }}>
                    {["Top 10%", "Top 7%", "Top 5%", "Top 4%", "Top 3%"].map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
              )}
            </div>
          </div>

          <div style={{ marginTop: 24, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
            <button className="search-btn" onClick={handleSearch} disabled={loadingListings || loadingAnalysis}>
              <Icon name="search" size={14} color="#fff" />
              {loadingListings ? "Fetching listings..." : loadingAnalysis ? "Analyzing..." : hasSearched ? "Re-run Search" : "Search Market"}
            </button>
            {error && <span style={{ fontSize: 11, color: "#e05050", background: "#fff5f5", border: "1px solid #fecaca", borderRadius: 8, padding: "6px 12px" }}>{error}</span>}
          </div>
        </div>

        {loadingListings && <div className="card"><Spinner message="Fetching real listings from RentCast..." /></div>}
        {!loadingListings && hasSearched && listings.length === 0 && !error && (
          <div className="card" style={{ textAlign: "center", padding: "40px 20px" }}>
            <div style={{ fontSize: 13, color: "var(--muted)" }}>No listings found. Try adjusting your filters.</div>
          </div>
        )}

        {!loadingListings && listings.length > 0 && (
          <div>
            <NeighborhoodMap listings={listings} />
            {loadingAnalysis && <div className="card"><Spinner message="Generating market analysis..." /></div>}
            {!loadingAnalysis && analysis && (
              <div className="card" style={{ background: "linear-gradient(135deg, var(--surface) 0%, #f8f6ff 100%)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <Icon name="sparkle" size={16} color="var(--accent)" />
                  <span style={{ fontSize: 11, letterSpacing: ".12em", color: "var(--muted)", textTransform: "uppercase" }}>{city} Rent Trend — Market Analysis</span>
                </div>
                {analysis.neighborhoodInsight && (
                  <div style={{ fontSize: 13, color: "var(--text)", margin: "8px 0 12px", lineHeight: 1.7, background: "var(--chip-bg)", borderRadius: 12, padding: "12px 14px", borderLeft: "3px solid var(--accent)" }}>{analysis.neighborhoodInsight}</div>
                )}
                <div style={{ fontSize: 11, color: "var(--accent)", fontWeight: 700, marginBottom: 16 }}>
                  Projected {movingDate}: ~${analysis.summary && analysis.summary.projectedRent ? analysis.summary.projectedRent.toLocaleString() : "0"}/mo
                </div>
                <ResponsiveContainer width="100%" height={175}>
                  <AreaChart data={analysis.rentTrend} margin={{ top: 4, right: 12, left: -12, bottom: 0 }}>
                    <defs>
                      <linearGradient id="rg" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6c47ff" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="#6c47ff" stopOpacity={0.01} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="month" tick={{ fontSize: 9, fill: "#aaa" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 9, fill: "#aaa" }} axisLine={false} tickLine={false} tickFormatter={v => "$" + v.toLocaleString()} domain={[rentMin, rentMax]} />
                    <Tooltip content={ChartTooltip} />
                    <ReferenceLine y={maxRent} stroke="#e05050" strokeDasharray="4 3" strokeWidth={1.5} label={{ value: "cap", position: "insideTopRight", fontSize: 9, fill: "#e05050", dy: -6 }} />
                    <Area type="monotone" dataKey="rent" stroke="#6c47ff" strokeWidth={2.5} fill="url(#rg)" dot={{ fill: "#6c47ff", r: 3.5, strokeWidth: 0 }} activeDot={{ r: 5 }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
            <Pagination pageSize={pageSize} setPageSize={setPageSize} page={page} setPage={setPage} total={allResults.length} />
            {visibleInside.map(l => <ListingCard key={l.id} listing={l} cap={isForSale ? maxListPrice : maxRent} dimmed={false} />)}
            {visibleOutside.length > 0 && (
              <div>
                <div style={{ fontSize: 11, letterSpacing: ".12em", color: "var(--muted)", textTransform: "uppercase", margin: "22px 0 10px", display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
                  Outside Your Current Filters
                  <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
                </div>
                {visibleOutside.map(l => <ListingCard key={l.id} listing={l} cap={isForSale ? maxListPrice : maxRent} dimmed />)}
              </div>
            )}
          </div>
        )}

        {!hasSearched && (
          <div className="card" style={{ textAlign: "center", padding: "60px 20px", background: "linear-gradient(135deg, var(--surface) 0%, #f8f6ff 100%)" }}>
            <div style={{ width: 64, height: 64, borderRadius: 20, background: "var(--accent-soft)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
              <Icon name="home" size={28} color="var(--accent)" />
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, fontFamily: "var(--font-display)", color: "var(--text)", marginBottom: 8 }}>Search any US market</div>
            <div style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.7, maxWidth: 380, margin: "0 auto" }}>
              Enter a city, set your filters, and hit Search Market. Real listings from RentCast plus AI-powered market analysis.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function App() {
  const getInitialPage = () => {
    try {
      const hash = window.location.hash.replace("#", "");
      if (hash === "childcare") return "childcare";
      if (hash === "explore") return "explore";
      if (hash === "travel") return "travel";
      return "homes";
    } catch { return "homes"; }
  };

  const [activePage, setActivePage] = useState(getInitialPage);
  const [copied, setCopied] = useState(false);
  const [homeShareUrl, setHomeShareUrl] = useState("https://movemtrc.com/#homes");
  const handleShareUrl = useCallback((url) => setHomeShareUrl(url), []);

  const handleTabChange = (page) => {
    setActivePage(page);
    try { window.history.replaceState(null, "", window.location.pathname + window.location.search + "#" + page); } catch {}
  };

  const handleShare = () => {
    const url = activePage === "homes" ? homeShareUrl : "https://movemtrc.com/#childcare";
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2500); });
    } else {
      const el = document.createElement("textarea");
      el.value = url; document.body.appendChild(el); el.select(); document.execCommand("copy"); document.body.removeChild(el);
      setCopied(true); setTimeout(() => setCopied(false), 2500);
    }
  };

  return (
    <div style={{ fontFamily: "var(--font-body)", background: "var(--bg)", minHeight: "100vh" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&family=Clash+Display:wght@600;700;800&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        :root {
          --font-display: 'Plus Jakarta Sans', sans-serif;
          --font-body: 'Plus Jakarta Sans', sans-serif;
          --accent: #6c47ff;
          --accent-light: #a78bff;
          --accent-soft: #ede8ff;
          --bg: #f4f2fb;
          --surface: #ffffff;
          --surface-dark: #1e1a2e;
          --border: #e8e3f8;
          --text: #1a1630;
          --muted: #8b87a8;
          --chip-bg: #f8f6ff;
          --shadow-card: 0 2px 12px rgba(108,71,255,.06);
          --shadow-card-hover: 0 8px 32px rgba(108,71,255,.15);
        }

        * { box-sizing: border-box; margin: 0; padding: 0; }
        html { -webkit-text-size-adjust: 100%; }

        @media (max-width: 600px) {
          .card { padding: 16px 14px !important; border-radius: 14px !important; }
          .search-btn { padding: 11px 20px !important; font-size: 12px !important; }
        }

        input[type=range] {
          -webkit-appearance: none; width: 100%; height: 4px;
          background: linear-gradient(to right, var(--accent) 0%, var(--border) 0%);
          border-radius: 2px; outline: none; cursor: pointer;
        }
        input[type=range]::-webkit-slider-thumb {
          -webkit-appearance: none; width: 20px; height: 20px; border-radius: 50%;
          background: var(--accent); border: 3px solid #fff;
          box-shadow: 0 2px 8px rgba(108,71,255,.35); cursor: pointer;
        }

        select, .txt-input {
          font-family: var(--font-body); font-size: 13px;
          background: var(--surface); border: 1.5px solid var(--border);
          border-radius: 12px; padding: 10px 14px; outline: none;
          color: var(--text); width: 100%; transition: border-color .15s, box-shadow .15s;
        }
        select:focus, .txt-input:focus {
          border-color: var(--accent);
          box-shadow: 0 0 0 3px rgba(108,71,255,.12);
        }
        .txt-input::placeholder { color: var(--muted); }

        .field-label {
          font-size: 11px; letter-spacing: .06em; color: var(--muted);
          font-weight: 600; margin-bottom: 8px; display: flex; align-items: center;
          text-transform: uppercase;
        }

        .search-btn {
          background: var(--accent); color: #fff; border: none; border-radius: 50px;
          padding: 13px 28px; font-family: var(--font-body); font-size: 13px;
          font-weight: 700; letter-spacing: .02em; cursor: pointer;
          transition: all .2s; display: flex; align-items: center; gap: 8px;
          box-shadow: 0 4px 16px rgba(108,71,255,.35);
        }
        .search-btn:hover { background: #5538e0; transform: translateY(-1px); box-shadow: 0 8px 24px rgba(108,71,255,.4); }
        .search-btn:active { transform: scale(0.98); }
        .search-btn:disabled { background: #c8c0e8; cursor: not-allowed; box-shadow: none; transform: none; }

        .card {
          background: var(--surface); border: 1.5px solid var(--border);
          border-radius: 20px; padding: 24px; margin-bottom: 20px;
          box-shadow: var(--shadow-card);
        }

        .pill-btn {
          font-family: var(--font-body); font-size: 11px; font-weight: 600;
          padding: 6px 14px; border-radius: 50px; text-decoration: none;
          display: inline-flex; align-items: center; gap: 5px; transition: all .15s;
        }
        .pill-btn-primary { background: var(--accent); color: #fff; }
        .pill-btn-primary:hover { background: #5538e0; }
        .pill-btn-danger { background: #e84545; color: #fff; }
        .pill-btn-danger:hover { background: #cc2020; }

        .leaflet-container { font-family: var(--font-body); }

        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* Nav */}
      <div style={{ background: "var(--surface)", borderBottom: "1.5px solid var(--border)", padding: "0 16px", display: "flex", alignItems: "center", flexWrap: "wrap", boxShadow: "0 2px 16px rgba(108,71,255,.06)", position: "sticky", top: 0, zIndex: 200 }}>
        {/* Logo */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginRight: 16, padding: "12px 0" }}>
          <div style={{ width: 30, height: 30, borderRadius: 9, background: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Icon name="sparkle" size={14} color="#fff" />
          </div>
          <span style={{ fontSize: 17, fontWeight: 800, fontFamily: "var(--font-display)", color: "var(--text)", whiteSpace: "nowrap" }}>
            Move<span style={{ color: "var(--accent)" }}>Metrics</span>
          </span>
        </div>

        {/* Tab buttons — Home Search, Find Childcare, Travel, Explore */}
        <button onClick={() => handleTabChange("homes")} style={{
          background: "none", border: "none", cursor: "pointer", fontFamily: "var(--font-body)",
          fontSize: 12, fontWeight: 600, color: activePage === "homes" ? "var(--accent)" : "var(--muted)",
          padding: "16px 12px", borderBottom: activePage === "homes" ? "2.5px solid var(--accent)" : "2.5px solid transparent",
          transition: "all .15s", display: "flex", alignItems: "center", gap: 5, whiteSpace: "nowrap",
        }}>
          <Icon name="home" size={14} color={activePage === "homes" ? "var(--accent)" : "var(--muted)"} />
          <span>Home Search</span>
        </button>

        <button onClick={() => handleTabChange("childcare")} style={{
          background: "none", border: "none", cursor: "pointer", fontFamily: "var(--font-body)",
          fontSize: 12, fontWeight: 600, color: activePage === "childcare" ? "var(--accent)" : "var(--muted)",
          padding: "16px 12px", borderBottom: activePage === "childcare" ? "2.5px solid var(--accent)" : "2.5px solid transparent",
          transition: "all .15s", display: "flex", alignItems: "center", gap: 5, whiteSpace: "nowrap",
        }}>
          <Icon name="heart" size={14} color={activePage === "childcare" ? "var(--accent)" : "var(--muted)"} />
          <span>Find Childcare</span>
        </button>

        <button onClick={() => handleTabChange("travel")} style={{
          background: "none", border: "none", cursor: "pointer", fontFamily: "var(--font-body)",
          fontSize: 12, fontWeight: 600, color: activePage === "travel" ? "var(--accent)" : "var(--muted)",
          padding: "16px 12px", borderBottom: activePage === "travel" ? "2.5px solid var(--accent)" : "2.5px solid transparent",
          transition: "all .15s", display: "flex", alignItems: "center", gap: 5, whiteSpace: "nowrap",
        }}>
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={activePage === "travel" ? "var(--accent)" : "var(--muted)"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21 4 19 4s-2 1-3.5 2.5L9 10 .8 8.2c-.5-.1-.7.5-.4.9L5 14l-1 4 4-1 4.6 4.6c.4.3 1-.1.9-.4z"/>
          </svg>
          <span>Travel</span>
        </button>

        <button onClick={() => handleTabChange("explore")} style={{
          background: "none", border: "none", cursor: "pointer", fontFamily: "var(--font-body)",
          fontSize: 12, fontWeight: 600, color: activePage === "explore" ? "var(--accent)" : "var(--muted)",
          padding: "16px 12px", borderBottom: activePage === "explore" ? "2.5px solid var(--accent)" : "2.5px solid transparent",
          transition: "all .15s", display: "flex", alignItems: "center", gap: 5, whiteSpace: "nowrap",
        }}>
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={activePage === "explore" ? "var(--accent)" : "var(--muted)"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
          </svg>
          <span>Explore</span>
        </button>

        {/* Share */}
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, padding: "8px 0" }}>
          <button onClick={handleShare} title="Copy shareable link" style={{
            display: "flex", alignItems: "center", gap: 6,
            background: copied ? "#e8faf0" : "var(--accent-soft)",
            border: "1.5px solid " + (copied ? "#1db96a" : "var(--accent)"),
            borderRadius: 50, padding: "6px 12px", cursor: "pointer",
            fontFamily: "var(--font-body)", fontSize: 12, fontWeight: 600,
            color: copied ? "#1db96a" : "var(--accent)", transition: "all .2s", whiteSpace: "nowrap",
          }}>
            {copied
              ? <><Icon name="check" size={13} color="#1db96a" /><span className="nav-label"> Copied!</span></>
              : <><Icon name="share" size={13} color="var(--accent)" /><span className="nav-label"> Share</span></>
            }
          </button>
          <a href={activePage === "homes" ? homeShareUrl : "https://movemtrc.com/#childcare"}
            target="_blank" rel="noreferrer" title="Open movemtrc.com"
            style={{
              display: "flex", alignItems: "center", gap: 5,
              background: "var(--surface-dark)", borderRadius: 50, padding: "6px 12px",
              fontFamily: "var(--font-body)", fontSize: 12, fontWeight: 600,
              color: "#fff", textDecoration: "none", whiteSpace: "nowrap",
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = "0.8"}
            onMouseLeave={e => e.currentTarget.style.opacity = "1"}
          >
            <Icon name="external" size={13} color="#fff" />
            <span className="nav-label">movemtrc.com</span>
          </a>
        </div>
      </div>

      <style>{`.nav-label { display: inline; } @media (max-width: 500px) { .nav-label { display: none; } }`}</style>

      <ScrollToTop />
      {activePage === "childcare" ? <FindChildcare />
        : activePage === "explore" ? <Explore />
        : activePage === "travel" ? <Travel />
        : <HomeSearch onShareUrl={handleShareUrl} />}
    </div>
  );
}
